import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Calendar, DollarSign, Target, FileText, Lock, Shield, XCircle, Receipt, Banknote, Package, ClipboardCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TrusteeWelcomeModal } from "@/components/trustee/TrusteeWelcomeModal";

export default function TrusteeDashboard() {
  const { toast } = useToast();
  const [policyEnabled, setPolicyEnabled] = useState(false);
  const [approvalThreshold, setApprovalThreshold] = useState(100);
  const [requireTwoCounters, setRequireTwoCounters] = useState(false);
  const [forceAssetAudit, setForceAssetAudit] = useState(false);
  const [requireRaApproval, setRequireRaApproval] = useState(true);
  const [loading, setLoading] = useState(true);
  const [expenseClaims, setExpenseClaims] = useState<any[]>([]);
  const [ptaId, setPtaId] = useState<string | null>(null);
  const [policyId, setPolicyId] = useState<string | null>(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeFirstName, setWelcomeFirstName] = useState("");

  useEffect(() => {
    fetchPtaId();
    const checkWelcome = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const [{ data: pref }, { data: profile }] = await Promise.all([
        supabase.from('user_preferences').select('preference_value').eq('user_id', user.id).eq('preference_key', 'trustee_welcome_seen').maybeSingle(),
        supabase.from('profiles').select('full_name,display_name').eq('id', user.id).maybeSingle(),
      ]);
      const name = profile?.full_name || profile?.display_name || "";
      setWelcomeFirstName(name.split(' ')[0] || "");
      if (pref?.preference_value !== 'true') setWelcomeOpen(true);
    };
    checkWelcome();
  }, []);

  useEffect(() => {
    if (ptaId) {
      fetchPolicy();
      fetchPendingApprovals();
    }
  }, [ptaId]);

  const fetchPtaId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .single();
    
    if (!error && data) setPtaId(data.pta_id);
  };

  const fetchPolicy = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('trustee_policies')
      .select('*')
      .eq('pta_id', ptaId)
      .maybeSingle();
    
    if (data) {
      setPolicyEnabled(data.enabled);
      setApprovalThreshold(data.approval_threshold);
      setRequireTwoCounters(data.require_two_counters);
      setForceAssetAudit(data.force_asset_audit || false);
      setRequireRaApproval((data as any).require_ra_approval ?? true);
      setPolicyId(data.id);
    }
    setLoading(false);
  };

  const fetchPendingApprovals = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from('expense_claims')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('status', 'waiting_approval')
      .order('created_at', { ascending: false });
    
    if (data) setExpenseClaims(data);
  };

  const handleSavePolicy = async () => {
    if (!ptaId) return;
    
    if (policyId) {
      const { error } = await supabase
        .from('trustee_policies')
        .update({
          enabled: policyEnabled,
          approval_threshold: approvalThreshold,
          require_two_counters: requireTwoCounters,
          force_asset_audit: forceAssetAudit,
          require_ra_approval: requireRaApproval,
        } as any)
        .eq('id', policyId);

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Policy updated",
          description: "Trustee policies have been saved.",
        });
      }
    } else {
      const { error } = await supabase
        .from('trustee_policies')
        .insert({
          enabled: policyEnabled,
          approval_threshold: approvalThreshold,
          require_two_counters: requireTwoCounters,
          force_asset_audit: forceAssetAudit,
          require_ra_approval: requireRaApproval,
          pta_id: ptaId
        } as any);

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Policy created",
          description: "Trustee policies have been saved.",
        });
        fetchPolicy();
      }
    }
  };

  const handleApprove = async (claimId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from('expense_claims')
      .update({ 
        status: 'approved',
        approved_at: new Date().toISOString(),
        approved_by: user?.id
      })
      .eq('id', claimId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Claim approved",
        description: "The expense claim has been approved by trustee.",
      });
      fetchPendingApprovals();
    }
  };

  const handleReject = async (claimId: string) => {
    const { error } = await supabase
      .from('expense_claims')
      .update({ 
        status: 'rejected',
        updated_at: new Date().toISOString()
      })
      .eq('id', claimId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Claim rejected",
        description: "The expense claim has been rejected.",
      });
      fetchPendingApprovals();
    }
  };

  const stats = [
    { title: "Total Members", value: "24", icon: Users },
    { title: "Active Goals", value: "3", icon: Target },
    { title: "Upcoming Events", value: "2", icon: Calendar },
    { title: "Funds Raised YTD", value: "£2,450", icon: DollarSign },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <TrusteeWelcomeModal
        open={welcomeOpen}
        firstName={welcomeFirstName}
        onDismiss={async (markSeen) => {
          setWelcomeOpen(false);
          if (markSeen) {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              await supabase.from('user_preferences').upsert({
                user_id: user.id,
                preference_key: 'trustee_welcome_seen',
                preference_value: 'true',
              }, { onConflict: 'user_id,preference_key' });
            }
          }
        }}
      />
      <div>
        <h1 className="text-3xl font-bold text-foreground">Trustee Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Read-only overview of PTA operations
        </p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
        <Lock className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
        <div>
          <h3 className="font-semibold text-blue-900 dark:text-blue-100">Read-Only Access</h3>
          <p className="text-sm text-blue-800 dark:text-blue-200">
            As a Trustee, you have view-only access to PTA information. You cannot make changes or edit data.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="shadow-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="summary" className="w-full">
        <TabsList>
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="financial">Financial Overview</TabsTrigger>
          <TabsTrigger value="activities">Activities</TabsTrigger>
          <TabsTrigger value="approvals" className="flex items-center gap-2">
            Approvals
            {expenseClaims.length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-5 flex items-center justify-center text-xs">
                {expenseClaims.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="policies">Policies</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle>Current Goals</CardTitle>
                <CardDescription>Active fundraising objectives</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="p-3 border border-border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">New playground equipment</span>
                      <span className="text-sm text-primary">£5,000</span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Progress</span>
                        <span>60%</span>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: "60%" }} />
                      </div>
                    </div>
                  </div>
                  <div className="p-3 border border-border rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Library books fund</span>
                      <span className="text-sm text-primary">£1,500</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Status: Up Next</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardHeader>
                <CardTitle>Recent Activities</CardTitle>
                <CardDescription>Latest PTA updates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                    <div>
                      <p className="text-sm font-medium">Summer Fair scheduled</p>
                      <p className="text-xs text-muted-foreground">2 days ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                    <div>
                      <p className="text-sm font-medium">New member approved</p>
                      <p className="text-xs text-muted-foreground">4 days ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                    <div>
                      <p className="text-sm font-medium">AGM minutes published</p>
                      <p className="text-xs text-muted-foreground">1 week ago</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="financial" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Financial Summary</CardTitle>
              <CardDescription>Current financial year overview</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 border border-border rounded-lg">
                    <p className="text-sm text-muted-foreground">Total Income</p>
                    <p className="text-2xl font-bold text-green-600">£2,450</p>
                  </div>
                  <div className="p-4 border border-border rounded-lg">
                    <p className="text-sm text-muted-foreground">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600">£1,125</p>
                  </div>
                  <div className="p-4 border border-border rounded-lg">
                    <p className="text-sm text-muted-foreground">Net Balance</p>
                    <p className="text-2xl font-bold text-primary">£1,325</p>
                  </div>
                </div>

                <div className="mt-6">
                  <h4 className="font-semibold mb-3">Recent Transactions</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div>
                        <p className="font-medium">Summer Fair Income</p>
                        <p className="text-sm text-muted-foreground">Jan 15, 2025</p>
                      </div>
                      <span className="text-green-600 font-semibold">+£450.00</span>
                    </div>
                    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div>
                        <p className="font-medium">Venue Hire</p>
                        <p className="text-sm text-muted-foreground">Jan 12, 2025</p>
                      </div>
                      <span className="text-red-600 font-semibold">-£75.00</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle>Upcoming Events</CardTitle>
                <CardDescription>Scheduled PTA events</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="p-3 border border-border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span className="font-medium">Annual General Meeting</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">June 15, 2025 at 7:00 PM</p>
                  </div>
                  <div className="p-3 border border-border rounded-lg">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span className="font-medium">Committee Meeting</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">April 10, 2025 at 6:30 PM</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardHeader>
                <CardTitle>Meeting Minutes</CardTitle>
                <CardDescription>Access to meeting records</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 rounded hover:bg-accent transition-colors cursor-pointer">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="text-sm">Planning Meeting - Jan 20</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded hover:bg-accent transition-colors cursor-pointer">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="text-sm">Committee - Dec 15</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="approvals" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Pending Trustee Approvals</CardTitle>
              <CardDescription>
                Expense claims exceeding £{approvalThreshold.toFixed(2)} that require your approval
              </CardDescription>
            </CardHeader>
            <CardContent>
              {expenseClaims.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No expense claims requiring approval
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Claimant</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenseClaims.map((claim) => (
                      <TableRow key={claim.id}>
                        <TableCell className="font-medium">{claim.claimant_name || 'Unknown'}</TableCell>
                        <TableCell>{new Date(claim.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                        <TableCell>{claim.description}</TableCell>
                        <TableCell className="font-semibold">£{claim.amount.toFixed(2)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleApprove(claim.id)}>
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleReject(claim.id)}>
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="policies" className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-xl font-semibold">PTA Policies</h2>
              <p className="text-sm text-muted-foreground">Configure the policies you want enforced across the PTA</p>
            </div>
          </div>

          {/* Expenses */}
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle>Expenses</CardTitle>
                  <CardDescription>Approval requirements for expense claims</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                  <div>
                    <p className="font-medium">Enable Approval Policy</p>
                    <p className="text-sm text-muted-foreground">Require trustee approval for large expenses</p>
                  </div>
                  <Switch checked={policyEnabled} onCheckedChange={setPolicyEnabled} disabled={loading} />
                </div>
                {policyEnabled && (
                  <div className="space-y-2">
                    <Label htmlFor="threshold">Approval Threshold (£)</Label>
                    <Input
                      id="threshold"
                      type="number"
                      step="0.01"
                      value={approvalThreshold}
                      onChange={(e) => setApprovalThreshold(parseFloat(e.target.value))}
                      placeholder="100.00"
                    />
                    <p className="text-sm text-muted-foreground">
                      Expenses over £{approvalThreshold.toFixed(2)} will require trustee approval before payment.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Floats */}
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Banknote className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle>Floats</CardTitle>
                  <CardDescription>Cash float counting requirements</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">Require Two Counters for Float Counting</p>
                  <p className="text-sm text-muted-foreground">Force float counting to require at least 2 members present</p>
                </div>
                <Switch checked={requireTwoCounters} onCheckedChange={setRequireTwoCounters} disabled={loading} />
              </div>
            </CardContent>
          </Card>

          {/* Assets */}
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle>Assets</CardTitle>
                  <CardDescription>Asset auditing requirements</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">Force Asset Audit</p>
                  <p className="text-sm text-muted-foreground">Asset role cannot disable asset audits when this is enabled</p>
                </div>
                <Switch checked={forceAssetAudit} onCheckedChange={setForceAssetAudit} disabled={loading} />
              </div>
            </CardContent>
          </Card>

          {/* Risk Assessments */}
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle>Risk Assessments</CardTitle>
                  <CardDescription>Approval requirements for risk assessments</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">Risk Assessments Require Approval</p>
                  <p className="text-sm text-muted-foreground">When enabled, drafts must be submitted to a Chair or Trustee for approval before being finalised</p>
                </div>
                <Switch checked={requireRaApproval} onCheckedChange={setRequireRaApproval} disabled={loading} />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardContent className="pt-6 space-y-4">
              <Button onClick={handleSavePolicy} disabled={loading} className="w-full">
                Save Policy Settings
              </Button>

              {policyEnabled && expenseClaims.length > 0 && (
                <div className="mt-2">
                  <h4 className="font-semibold mb-3">Pending Approvals ({expenseClaims.length})</h4>
                  <div className="space-y-3">
                    {expenseClaims.map((claim) => (
                      <div key={claim.id} className="p-4 border border-border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-semibold">{claim.description}</h5>
                            <p className="text-sm text-muted-foreground">
                              {claim.claimant_name} - £{claim.amount.toFixed(2)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Submitted: {new Date(claim.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button size="sm" onClick={() => handleApprove(claim.id)}>
                            Approve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {policyEnabled && expenseClaims.length === 0 && (
                <div className="p-4 border border-border rounded-lg bg-muted/30 text-center">
                  <p className="text-sm text-muted-foreground">No expenses awaiting approval</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
